import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { boatSchema, type BoatInput } from '../lib/boats';
import FileUploadField from './FileUploadField';
import ApprovalBadge from './ApprovalBadge';
import AdminApprovalSection, { type ApprovalSavePayload } from './AdminApprovalSection';
import type { Timestamp } from 'firebase/firestore';

interface ApprovalView {
  approved: boolean;
  approvedAt: Timestamp | Date | null;
  approvedBy: string | null;
  note: string | null;
}

interface Props {
  boatId: string;
  initial: BoatInput;
  approval?: ApprovalView | null;
  mode?: 'self' | 'admin';
  onSave: (values: BoatInput) => Promise<void>;
  onApprovalSave?: (payload: ApprovalSavePayload) => Promise<void>;
}

export default function BoatForm({ boatId, initial, onSave, approval, mode = 'self', onApprovalSave }: Props) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<BoatInput>({
    resolver: zodResolver(boatSchema),
    defaultValues: initial,
  });
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  async function onSubmit(values: BoatInput) {
    setServerError(null);
    try {
      await onSave(values);
      setSavedAt(new Date());
    } catch (e) {
      setServerError(e instanceof Error ? e.message : 'Kunde inte spara.');
    }
  }

  const yearError = errors.modelYear ? 'Årsmodell ogiltig (1900–innevarande år).' : null;
  const hpError = errors.engine?.powerHp ? 'Effekt ogiltig (heltal 0–2000).' : null;

  const sectionTitle =
    'mt-6 mb-2 text-sm font-semibold tracking-wider text-navy uppercase border-b pb-1';

  function input(name: keyof BoatInput, label: string, type = 'text') {
    return (
      <label className="block">
        <span className="text-sm font-medium">{label}</span>
        <input
          type={type}
          className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-sky focus:ring-sky"
          {...register(name as never, {
            setValueAs: (v) =>
              v === '' ? null : type === 'number' ? Number(v) : v,
          })}
        />
      </label>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-2 max-w-xl">
      <h2 className={sectionTitle}>Båt</h2>
      {input('name', 'Namn')}
      {input('model', 'Modell')}
      {input('hullSerialNumber', 'Skrov-HIN')}
      <div className="grid grid-cols-2 gap-4 items-end">
        {input('modelYear', 'Årsmodell', 'number')}
        <label className="flex items-center gap-2 pb-2">
          <input type="checkbox" {...register('modelYearUnknown')} />
          <span className="text-sm">Vet ej</span>
        </label>
      </div>
      {yearError && <p className="text-bad text-sm">{yearError}</p>}

      <h2 className={sectionTitle}>Motor</h2>
      <Controller
        control={control}
        name="engine"
        render={({ field }) => (
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Märke</span>
              <input
                type="text"
                className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-sky focus:ring-sky"
                value={field.value?.brand ?? ''}
                onChange={(e) =>
                  field.onChange({
                    brand: e.target.value || null,
                    serialNumber: field.value?.serialNumber ?? null,
                    powerHp: field.value?.powerHp ?? null,
                  })
                }
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Serienummer</span>
              <input
                type="text"
                className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-sky focus:ring-sky"
                value={field.value?.serialNumber ?? ''}
                onChange={(e) =>
                  field.onChange({
                    brand: field.value?.brand ?? null,
                    serialNumber: e.target.value || null,
                    powerHp: field.value?.powerHp ?? null,
                  })
                }
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Effekt (hk)</span>
              <input
                type="number"
                className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-sky focus:ring-sky"
                value={field.value?.powerHp ?? ''}
                onChange={(e) =>
                  field.onChange({
                    brand: field.value?.brand ?? null,
                    serialNumber: field.value?.serialNumber ?? null,
                    powerHp: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />
            </label>
            {hpError && <p className="text-bad text-sm">{hpError}</p>}
          </div>
        )}
      />

      <h2 className={sectionTitle}>Försäkring</h2>
      {input('insuranceCompany', 'Bolag')}

      <h2 className={sectionTitle}>Bottenfärg</h2>
      <Controller
        control={control}
        name="measurementCert"
        render={({ field }) => (
          <FileUploadField
            boatId={boatId}
            label="Mätintyg"
            value={field.value?.fileRef ?? null}
            onChange={(ref) =>
              field.onChange(
                ref
                  ? { exists: true, issuedAt: field.value?.issuedAt ?? null, fileRef: ref }
                  : null,
              )
            }
          />
        )}
      />
      <Controller
        control={control}
        name="selfDeclaration"
        render={({ field }) => (
          <div className="space-y-2 mt-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={field.value?.given ?? false}
                onChange={(e) =>
                  field.onChange(
                    e.target.checked
                      ? {
                          given: true,
                          basis: field.value?.basis ?? null,
                          fileRefs: field.value?.fileRefs ?? [],
                          declaredAt: field.value?.declaredAt ?? new Date(),
                        }
                      : null,
                  )
                }
              />
              <span className="text-sm font-medium">Egenintyg</span>
            </label>
            {field.value?.given && (
              <>
                <label className="block">
                  <span className="text-sm font-medium">Beskrivning</span>
                  <textarea
                    rows={3}
                    className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-sky focus:ring-sky"
                    value={field.value.basis ?? ''}
                    onChange={(e) =>
                      field.onChange({
                        ...field.value,
                        basis: e.target.value || null,
                      })
                    }
                  />
                </label>
                {field.value.fileRefs.map((ref, i) => (
                  <div key={ref} className="flex items-start gap-3">
                    <div className="flex-1">
                      <FileUploadField
                        boatId={boatId}
                        label={`Bilaga ${i + 1}`}
                        value={ref}
                        onChange={(newRef) => {
                          if (!newRef) return;
                          const next = [...field.value!.fileRefs];
                          next[i] = newRef;
                          field.onChange({ ...field.value!, fileRefs: next });
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      aria-label={`Ta bort bilaga ${i + 1}`}
                      onClick={() =>
                        field.onChange({
                          ...field.value!,
                          fileRefs: field.value!.fileRefs.filter(
                            (_, j) => j !== i,
                          ),
                        })
                      }
                      className="text-bad text-sm underline mt-7"
                    >
                      Ta bort
                    </button>
                  </div>
                ))}
                <FileUploadField
                  key={`add-${field.value.fileRefs.length}`}
                  boatId={boatId}
                  label={
                    field.value.fileRefs.length === 0
                      ? 'Lägg till bilaga (valfri)'
                      : '+ Lägg till bilaga'
                  }
                  value={null}
                  onChange={(ref) => {
                    if (!ref) return;
                    field.onChange({
                      ...field.value!,
                      fileRefs: [...field.value!.fileRefs, ref],
                    });
                  }}
                />
              </>
            )}
          </div>
        )}
      />

      {approval && approval.approved && (
        <div className="mt-4 p-3 bg-green-50 rounded">
          <ApprovalBadge approval={approval} />
          {approval.note && (
            <p className="text-sm mt-1">Anteckning: {approval.note}</p>
          )}
        </div>
      )}

      {serverError && <p className="text-bad text-sm">{serverError}</p>}
      {savedAt && (
        <p className="text-ok text-sm" role="status">
          Sparat {savedAt.toLocaleTimeString('sv-SE')}
        </p>
      )}
      <button
        type="submit"
        disabled={isSubmitting}
        className="bg-sky text-white rounded px-4 py-2 font-medium disabled:opacity-50"
      >
        {isSubmitting ? 'Sparar…' : 'Spara'}
      </button>

      {mode === 'admin' && onApprovalSave && (
        <AdminApprovalSection
          approval={approval ?? null}
          onSave={onApprovalSave}
        />
      )}
    </form>
  );
}
